import { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Divider,
  Button,
  Toolbar,
  IconButton,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate, useParams } from 'react-router';
import { ArrowBack } from '@mui/icons-material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ManageHistoryOutlinedIcon from '@mui/icons-material/ManageHistoryOutlined';
import CloseIcon from '@mui/icons-material/Close';
import Paper from '@mui/material/Paper';
import APP_ROLES from '@/components/AppRoles';
import { useUser } from '@/providers/UserContext';
import LoadingMask from '@/components/shared/LoadingMask';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import { useAuth } from '@/providers/AuthContext';
import { useApi } from '@/providers/ApiContext';
import {
  getDocument,
  deleteDocument,
  rerunDocumentProcessing,
} from '@/actions/document';
import { getDocumentAsset } from '@/actions/documentAsset';
import DocumentDeleteDialog from '../DocumentDeleteDialog';
import DocumentViewDialog from '@/components/shared/DocumentViewDialog';
import toNiceTimestamp from '@/util/toNiceTimestamp';
import { useDocTypes } from '@/providers/DocTypesContext';
import type { Document, EntityId } from '@/types';

function AdminDocument(): React.JSX.Element {
  const user = useUser();
  const auth = useAuth();
  const api = useApi();
  const navigate = useNavigate();
  const doc_types = useDocTypes();
  const { document_id } = useParams<{ document_id: string }>();
  const [document, setDocument] = useState<Document | null>(null);
  const [loading_document, setLoadingDocument] = useState<boolean>(true);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [docOpen, setDocOpen] = useState<boolean>(false);
  const [doc_content, setDocContent] = useState<unknown>(null);
  const [doc_title, setDocTitle] = useState<string | null>(null);
  const [doc_type, setDocType] = useState<string | null>(null);
  const [sections, setSections] = useState<unknown>(null);
  const [pages, setPages] = useState<unknown>(null);
  const [sectionsOpen, setSectionsOpen] = useState<boolean>(false);
  const [api_error, setApiError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.entity_id && !document && document_id && api) {
      getDocument(api, document_id)
        .then((data) => {
          setDocument(data);
          setLoadingDocument(false);
        })
        .catch((error) => {
          console.log(error);
          setLoadingDocument(false);
          navigate('/error', {
            state: {
              errorMessage: 'Failed to load document',
              errorDetails:
                (error as Error).message ||
                'Unable to retrieve the requested document. This could be due to network issues, server problems, or the document may no longer exist.',
              returnPath: `/admin/documents/${document_id}`,
            },
          });
        });
    }
  }, [api, user?.entity_id, document, document_id, navigate]);

  useEffect(() => {
    if (document?.content_id && doc_content === null && api) {
      getDocumentAsset(
        api,
        document.entity_id,
        `${document.content_id}/manifest.json`,
      )
        .then(
          (document_manifest: {
            type?: string;
            sections?: string;
            pages?: string;
            formatted?: string;
            name?: string;
          }) => {
            setDocType(document_manifest?.type || null);
            if (document_manifest?.sections && api) {
              getDocumentAsset(
                api,
                document.entity_id,
                `${document.content_id}/${document_manifest.sections}`,
              )
                .then((data) => {
                  setSections(data);
                })
                .catch((error) => {
                  console.log(error);
                  setSections(null);
                });
            }
            if (document_manifest?.pages && api) {
              getDocumentAsset(
                api,
                document.entity_id,
                `${document.content_id}/${document_manifest.pages}`,
              )
                .then((data) => {
                  setPages(data);
                })
                .catch((error) => {
                  console.log(error);
                  setPages(null);
                });
            }
            if (document_manifest?.formatted && api) {
              getDocumentAsset(
                api,
                document.entity_id,
                `${document.content_id}/${document_manifest.formatted}`,
              )
                .then((data) => {
                  setDocContent(data);
                  setDocTitle(document_manifest?.name || null);
                })
                .catch((error) => {
                  console.log(error);
                  setDocContent('');
                  setDocTitle('');
                });
            }
          },
        )
        .catch((error) => {
          console.log(error);
          setDocContent('');
          setDocTitle('');
          setSections(null);
          setPages(null);
        });
    }
  }, [document, doc_content, api]);

  const handleDeleteDocument = async (document_id: EntityId): Promise<void> => {
    if (!api) return;
    try {
      await deleteDocument(api, document_id);
      if (document && doc_types) {
        const doc_type = doc_types.find(
          (doc_type) => doc_type.entity_id === document.document_type_id,
        );
        if (doc_type) {
          navigate({
            pathname: '/admin/documents',
            search: `?type=${doc_type.code}`,
          });
        }
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to delete the document. This could be due to network issues, server problems, or insufficient permissions.',
      );
    }
  };

  // JSON dialog for sections
  const renderSectionsDialog = (
    open: boolean,
    handleClose: () => void,
    docContent: unknown,
    sections: unknown,
    pages: unknown,
    title: string,
  ): React.JSX.Element => {
    const has_pages = Boolean(pages);

    return (
      <Dialog onClose={handleClose} open={open} maxWidth='xl' fullWidth>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <b>{title}</b>
            <IconButton aria-label='close' onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box padding='10px' sx={{ maxWidth: 1800 }}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant='subtitle1' sx={{ mb: 1 }}>
                  Sections
                </Typography>
                <Paper
                  variant='outlined'
                  sx={{
                    p: 1,
                    backgroundColor: '#f5f5f5',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    maxHeight: '600px',
                    overflow: 'auto',
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(sections, null, 2)}
                  </pre>
                </Paper>
              </Grid>
              {has_pages && (
                <Grid size={12}>
                  <Typography variant='subtitle1' sx={{ mb: 1 }}>
                    Pages
                  </Typography>
                  <Paper
                    variant='outlined'
                    sx={{
                      p: 1,
                      backgroundColor: '#f5f5f5',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      maxHeight: '600px',
                      overflow: 'auto',
                    }}
                  >
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(pages, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              )}
              <Grid size={12}>
                <Typography variant='subtitle1' sx={{ mb: 1 }}>
                  Document Content
                </Typography>
                <Paper
                  variant='outlined'
                  sx={{
                    p: 1,
                    backgroundColor: '#f5f5f5',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    maxHeight: '600px',
                    overflow: 'auto',
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {typeof docContent === 'object'
                      ? JSON.stringify(docContent, null, 2)
                      : String(docContent)}
                  </pre>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  if (loading_document || !document) {
    return <LoadingMask />;
  }

  return (
    <>
      <Toolbar
        sx={{
          minHeight: '55px !important',
        }}
      >
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Typography variant='h6' sx={{ mr: 4 }}>
          {document.name} Document
        </Typography>
        <Stack direction={'row'} spacing={2}>
          <Button
            onClick={() => {
              navigate({
                pathname: '/admin/documents/edit',
                search: `?document_id=${document.entity_id}`,
              });
            }}
            startIcon={<EditOutlinedIcon />}
          >
            Edit
          </Button>
          <Button
            onClick={() => setDeleteOpen(true)}
            startIcon={<DeleteOutlinedIcon />}
          >
            Delete
          </Button>
        </Stack>
      </Toolbar>
      <Divider />

      <Box padding='15px'>
        <Typography variant='subtitle1'>Basic Information</Typography>
        <Divider />
        <br />
        <Grid
          container
          component='dl' // mount a Definition List
          spacing={0}
          sx={{ display: 'table' }}
        >
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>ID:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {document.entity_id}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Document ID:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {document.doc_id}
            </Typography>
          </Grid>
          {doc_type && (
            <Grid>
              <Typography
                component='dt'
                variant='body1'
                sx={{ display: 'table-cell', width: '250px' }}
              >
                <b>Document Type:</b>
              </Typography>
              <Typography
                component='dd'
                variant='body2'
                sx={{ display: 'table-cell' }}
              >
                {doc_type}
              </Typography>
            </Grid>
          )}
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Status:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {document.status}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Created:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {toNiceTimestamp(document.created_at, true)}
            </Typography>
          </Grid>
          {(auth?.roles || []).includes(APP_ROLES.ADMIN_ACCQSURE) ? (
            <>
              <Grid>
                <br />
                <Divider />
                <br />
              </Grid>
              <Grid>
                <Typography variant='subtitle1'>
                  AccQsure Admin Tools
                </Typography>
              </Grid>
              <Grid>
                <Button
                  onClick={() => {
                    if (!api) return;
                    rerunDocumentProcessing(api, document.entity_id)
                      .then(() => {
                        if (doc_types) {
                          const doc_type = doc_types.find(
                            (doc_type) =>
                              doc_type.entity_id === document.document_type_id,
                          );
                          if (doc_type) {
                            navigate({
                              pathname: '/admin/documents',
                              search: `?type=${doc_type.code}`,
                            });
                          }
                        }
                      })
                      .catch((error) => {
                        console.error('Error reprocessing document:', error);
                        const err = error as Error;
                        setApiError(
                          err.message ||
                            'Unable to reprocess the document. This could be due to network issues, server problems, or the document may be in an invalid state.',
                        );
                      });
                  }}
                  startIcon={<ManageHistoryOutlinedIcon />}
                >
                  Reprocess
                </Button>
                {Boolean(sections) && (
                  <Button
                    onClick={() => setSectionsOpen(true)}
                    startIcon={<EditOutlinedIcon />}
                  >
                    View Contents
                  </Button>
                )}
              </Grid>
            </>
          ) : undefined}

          {Boolean(doc_content) ? (
            <>
              <Grid>
                <br />
                <Divider />
                <br />
              </Grid>
              <Grid>
                <Link
                  component='button'
                  variant='subtitle1'
                  onClick={() => {
                    setDocOpen(true);
                  }}
                >
                  <Typography variant='h6'>View Document</Typography>
                </Link>
              </Grid>
            </>
          ) : undefined}
        </Grid>
      </Box>

      <DocumentDeleteDialog
        document_id={document.entity_id}
        deleteDocument={handleDeleteDocument}
        open={deleteOpen}
        handleClose={() => setDeleteOpen(false)}
      />

      <DocumentViewDialog
        title={doc_title}
        contents={doc_content}
        open={docOpen}
        handleClose={() => setDocOpen(false)}
      />
      {renderSectionsDialog(
        sectionsOpen,
        () => setSectionsOpen(false),
        doc_content,
        sections,
        pages,
        'Document Content & Sections',
      )}

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </>
  );
}

export default AdminDocument;
